import { useState } from 'react';
import { cn } from '@/lib/utils';

// Deterministic per-name palette FALLBACK for names without a saved profile
// avatar. When the employee's profile has an avatar image or chosen color
// (profiles.avatar_url / avatar_color — same fields the sidebar UserAvatar
// uses), those win.
const AVATAR_COLORS = [
  'bg-sky-600',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-rose-600',
  'bg-amber-600',
  'bg-teal-600',
  'bg-indigo-600',
  'bg-fuchsia-600',
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function colorOf(name: string): string {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) % 100003;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

interface InitialsAvatarProps {
  name: string;
  /** Saved profile avatar image; shown instead of initials when present */
  avatarUrl?: string | null;
  /** Saved profile avatar color (hex) for the initials background */
  avatarColor?: string | null;
  className?: string;
}

/**
 * Small circular employee badge: the profile's avatar photo when saved,
 * otherwise initials on the profile's chosen color (deterministic palette as
 * a last resort). Full name on hover.
 */
export default function InitialsAvatar({
  name,
  avatarUrl,
  avatarColor,
  className,
}: InitialsAvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);

  if (avatarUrl && !imageFailed) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        title={name}
        onError={() => setImageFailed(true)}
        className={cn(
          'inline-block h-5 w-5 shrink-0 select-none rounded-full object-cover',
          className
        )}
      />
    );
  }

  return (
    <span
      title={name}
      aria-label={name}
      className={cn(
        'inline-flex h-5 w-5 shrink-0 select-none items-center justify-center rounded-full text-[9px] font-semibold leading-none text-white',
        !avatarColor && colorOf(name),
        className
      )}
      style={avatarColor ? { backgroundColor: avatarColor } : undefined}
    >
      {initialsOf(name)}
    </span>
  );
}
